import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of, race } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, map, take, tap, withLatestFrom } from 'rxjs/operators';
import { ChangeLogEntry, TherapyLimitations } from '@mona/models';
import {
    applyInstancesChanges,
    ChangeLogAction,
    ChangeLogSelectors,
    getPersistedChangesData,
    getRevertedChangesForModel,
    getRevertedCreateIds,
    reconcileInstancesWithChanges,
} from '@mona/pdms/data-access-changelog';
import {
    applyLastChangedByToPrescriptions,
    currentEncounterId,
    getCurrentEncounterProp,
    isEmptyEncounterId,
} from '@mona/pdms/data-access-combined';
import { PractitionersFacade } from '@mona/pdms/data-access-practitioners';
import { isSameEncounterFilter } from '@mona/shared/utils';
import { TherapyLimitationsApi } from '../../infrastructure';
import { TherapyLimitationsActions } from '../actions';
import { selectTherapyLimitationsAll } from '../selectors';

/**
 * TherapyLimitations effects
 */
@Injectable()
export class TherapyLimitationsEffects {
    changeLogMap$ = this.store.select(ChangeLogSelectors.getChangesMap);
    therapyLimitationsAll$ = this.store.select(selectTherapyLimitationsAll);

    /** Load Therapy Limitations */
    loadTherapyLimitations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TherapyLimitationsActions.loadTherapyLimitations),
            withLatestFrom(getCurrentEncounterProp('patientId')),
            currentEncounterId(this.store),
            concatMap(([[, patientId], encounterId]) =>
                race(
                    isEmptyEncounterId(this.store).pipe(
                        take(1),
                        map(() =>
                            TherapyLimitationsActions.loadTherapyLimitationsCancelled({ reason: 'encounter-cleared' }),
                        ),
                    ),
                    this.therapyLimitationsApi.getTherapyLimitations(patientId).pipe(
                        isSameEncounterFilter(currentEncounterId(this.store), encounterId),
                        withLatestFrom(this.changeLogMap$, this.practitionersFacade.practitionersMap$),
                        map(([data, changesMap, practitionersMap]) => {
                            const changes = changesMap['TherapyLimitations'] || [];

                            if (changes.length) {
                                data = applyInstancesChanges(data, changes) as TherapyLimitations[];
                            }

                            data = applyLastChangedByToPrescriptions(data, practitionersMap);

                            return TherapyLimitationsActions.loadTherapyLimitationsSuccess({ data });
                        }),
                        catchError(error => of(TherapyLimitationsActions.loadTherapyLimitationsFailure({ error }))),
                        defaultIfEmpty(
                            TherapyLimitationsActions.loadTherapyLimitationsCancelled({
                                reason: 'encounter-changed',
                            }) as Action,
                        ),
                    ),
                ),
            ),
        ),
    );

    /** Listen change save success & upsert entity + show message */
    onChangeSaved$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ChangeLogAction.saveChangeAction.action,
                    ChangeLogAction.saveChangeAction.succeededAction,
                    ChangeLogAction.saveChangesAction.action,
                    ChangeLogAction.saveChangesAction.succeededAction,
                    ChangeLogAction.revertOptimisticChange,
                    ChangeLogAction.revertOptimisticChanges,
                    ChangeLogAction.revertStaleSavingEntries,
                ),
                withLatestFrom(this.therapyLimitationsAll$, this.changeLogMap$),
                tap(([action, data, changesMap]) => {
                    const changes: ChangeLogEntry<TherapyLimitations>[] = changesMap['TherapyLimitations'] || [];
                    const reverted = getRevertedChangesForModel(action, 'TherapyLimitations');
                    const therapyLimitations = reconcileInstancesWithChanges(data, changes, reverted);

                    // A reverted optimistic create is filtered out of the reconciled array, but
                    // `upsertMany` never removes entities — so the phantom row would linger with
                    // its `isSaving` flag set. Explicitly remove reverted create ids (mirrors the
                    // entity-adapter effects, e.g. AdditionalDevices).
                    const revertedCreateIds = getRevertedCreateIds(reverted);
                    if (revertedCreateIds.length) {
                        this.store.dispatch(
                            TherapyLimitationsActions.removeTherapyLimitations({ ids: revertedCreateIds }),
                        );
                    }

                    if (therapyLimitations) {
                        this.store.dispatch(
                            TherapyLimitationsActions.upsertTherapyLimitations({ data: therapyLimitations }),
                        );
                    }
                }),
            ),
        { dispatch: false },
    );

    /** Listen change persist success & add one - realistic update */
    onChangePersisted$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.persistChangesAction.succeededAction),
                withLatestFrom(
                    this.therapyLimitationsAll$,
                    this.changeLogMap$,
                    this.practitionersFacade.practitionersMap$,
                ),
                tap(([, data, changesMap, practitionersMap]) => {
                    const changes = changesMap['TherapyLimitations'] || [];

                    if (changes.length) {
                        const removeUpdateData = getPersistedChangesData(data, changes);
                        removeUpdateData.toUpdate = applyLastChangedByToPrescriptions(
                            removeUpdateData.toUpdate,
                            practitionersMap,
                        );

                        this.store.dispatch(
                            TherapyLimitationsActions.addTherapyLimitationsChanges({
                                toUpdateEntities: removeUpdateData.toUpdate || [],
                                toRemoveIds: removeUpdateData.toRemove.map(({ id }) => id) || [],
                            }),
                        );
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * Constructor
     * @param store
     * @param actions$
     * @param therapyLimitationsApi
     * @param practitionersFacade
     */
    constructor(
        private store: Store<any>,
        private actions$: Actions,
        private therapyLimitationsApi: TherapyLimitationsApi,
        private practitionersFacade: PractitionersFacade,
    ) {}
}
